import React from 'react';
import { Linkedin } from 'lucide-react';

const Founder = () => {
  // Previous Work logos
  const previousWork = [
    { name: 'Mena', logo: '/Previous Work/mena.png', invert: false },
    { name: 'Paymob', logo: '/Previous Work/paymob.jpeg', invert: false },
    { name: 'Mastercard', logo: '/Previous Work/Mastercard.svg', invert: false },
    { name: 'Visa', logo: '/Previous Work/visa.svg', invert: false },
    { name: 'Payswiff', logo: '/Previous Work/Payswiff.jpeg', invert: false },
    { name: 'Yahoo', logo: '/Previous Work/yahoo.svg', invert: false },
  ];

  // Education logos
  const education = [
    { name: 'INSEAD', logo: '/Education/Insead.png', invert: false },
    { name: 'UPenn', logo: '/Education/UPenn.png', invert: false },
    { name: 'Harvard', logo: '/Education/Harvard.svg', invert: false },
    { name: 'Heriot-Watt University', logo: '/Education/Heriot-Watt_University_arms.svg.png', invert: false },
    { name: 'AUC', logo: '/Education/AUC.jpeg', invert: false },
  ];

  // Language flags
  const languages = [
    { name: 'English', flag: '/Languages/US.svg' },
    { name: 'French', flag: '/Languages/France.svg' },
    { name: 'Arabic', flag: '/Languages/Egypt.svg' },
    { name: 'Arabic', flag: '/Languages/Morocco.svg' },
    { name: 'Spanish', flag: '/Languages/Spain.svg' },
    { name: 'Portuguese', flag: '/Languages/Portugal.svg' },
    { name: 'Italian', flag: '/Languages/Italy.svg' },
  ];

  return (
    <section id="founder" className="section-padding relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(73,17,28,0.15)_0%,transparent_70%)] pointer-events-none"></div>
      
      <div className="container mx-auto px-4 py-12 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10 sm:mb-16">
          <h1 className="section-title mx-auto text-3xl sm:text-4xl lg:text-5xl">Our Founder</h1>
        </div>
        
        <div className="flex flex-col gap-8 lg:flex-row lg:items-start max-w-7xl mx-auto">
          {/* Left Column - Image */}
          <div className="w-full lg:w-1/2">
            <img 
              src="/Imane1.JPG"
              alt="Imane Adel - Founder and CEO"
              className="w-full h-auto max-h-[500px] sm:max-h-[600px] lg:max-h-[700px] object-cover rounded-xl shadow-xl"
              loading="eager"
            />
          </div>

          {/* Right Column - Details */}
          <div className="w-full lg:w-1/2 flex flex-col gap-6">
            {/* Bio Card */}
            <div className="bg-gradient-to-br from-black/30 via-black/20 to-transparent backdrop-blur-sm rounded-lg p-4 sm:p-6 border border-accent/20 flex-grow">
              <div className="mb-4 sm:mb-6">
                <div className="flex items-center justify-between flex-col sm:flex-row gap-4 sm:gap-0">
                  <div className="flex flex-col gap-1 text-center sm:text-left">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white">Imane Adel</h1>
                    <p className="text-sm sm:text-base text-white/90">Founder & CEO</p>
                  </div>
                  <a 
                    href="https://www.linkedin.com/in/imaneadel/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white hover:text-accent transition-all duration-300"
                  >
                    <Linkedin size={24} />
                  </a>
                </div>
              </div>

              <div className="prose prose-invert max-w-none">
                <p className="text-white/90 text-sm sm:text-base leading-relaxed mb-4">
                  Imane Adel is a visionary leader and strategic advisor with over 15 years of experience shaping payments, fintech, and multi-sector technology, with deep expertise in blockchain and AI. As Founder and CEO of QuantumSphere, she crafts bespoke solutions that drive transformative growth for global enterprises and high-growth startups. Her cross-cultural acumen and expansive reach, honed across dynamic markets, empower clients to innovate and succeed.
                </p>
                <p className="text-white/90 text-sm sm:text-base leading-relaxed">
                  A compelling global speaker, Imane fuses blockchain, AI, and fintech insights with lessons from extreme sports like skydiving, inspiring audiences on leadership, resilience, and peak performance. Featured in Forbes and Entrepreneur, she advises influential boards, notably shaped regional fintech as former Co-Chair of the MENA Fintech Association's Payments Working Group, and mentors ventures via DIFC Fintech Hub, Ignyte, and Abu Dhabi SME hub and others.
                </p>
              </div>
            </div>

            {/* Languages Card */}
            <div className="bg-gradient-to-br from-black/30 via-black/20 to-transparent backdrop-blur-sm rounded-lg p-4 sm:p-6 border border-accent/20">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Expertise and support in multi-languages.</h3>
              <div className="flex flex-wrap gap-2 justify-start">
                {languages.map((lang, index) => (
                  <div 
                    key={index}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden border-2 border-accent/20 hover:border-accent/40 transition-all duration-300 shadow-lg"
                  >
                    <img 
                      src={lang.flag} 
                      alt={lang.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Experience & Education Section */}
        <div className="mt-12">
          <div className="bg-gradient-to-br from-black/30 via-black/20 to-transparent backdrop-blur-sm rounded-lg p-6 sm:p-8 border border-accent/20">
            {/* Previously Served At */}
            <h3 className="text-base sm:text-lg font-semibold text-white mb-6 text-center">Previously Served At</h3>
            <div className="flex flex-wrap gap-4 sm:gap-6 justify-center items-center">
              {previousWork.map((company, index) => (
                <div 
                  key={index}
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden flex items-center justify-center"
                >
                  <img 
                    src={company.logo} 
                    alt={company.name}
                    className="w-full h-full object-contain"
                  />
                </div>
              ))}
            </div>

            {/* Education */}
            <h3 className="text-base sm:text-lg font-semibold text-white mb-6 text-center mt-12">Education</h3>
            <div className="flex flex-wrap gap-4 sm:gap-6 justify-center items-center">
              {education.map((inst, index) => (
                <div 
                  key={index}
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden"
                >
                  <img 
                    src={inst.logo} 
                    alt={inst.name}
                    className={`w-full h-full object-contain ${inst.invert ? 'invert' : ''}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Founder;